import type { LocalDict } from "../../core/i18n";

// The sidebar bot list's own strings (views/nav/BotNav.tsx). Shared keys
// resolve through the useT fallback into the central dictionary.
//
// Every control carries its own explanation: what it changes, what happens
// when it is used, and what each choice means. A menu item without one is a
// defect — the right-click menu is the only door to most of this.
export const STR: LocalDict = {
  en: {
    "bots.chats": "Group chats",
    "bots.chatsEmpty": "No group chats yet.",
    "bots.chatsLoading": "Loading group chats…",
    "bots.chatsMenuLabel": "Actions for the group chats",
    "bots.newChatGroup": "New group chat…",
    "bots.newChatGroupHelp":
      "Starts a conversation that holds 2–6 of your agents at once. Every agent in it reads every message; the first one you pick answers anything that names nobody, and the others reply when you @mention them. Not the same as a group in this list — those only sort agents.",
    "bots.newChatGroupNeedsTwo": "You need at least two agents to start a group chat.",
    "bots.openChat": "Open",
    "bots.openChatHelp": "Opens this group conversation.",
    "bots.openChatTitle": "{name} — {members}",
    "bots.sharedChat": "Shared",
    "bots.sharedChatHelp":
      "Somebody else made this group chat and shared it with you. You can read it and post in it; only its creator and admins can rename it, change its agents, share it further or delete it. Open its details to leave it.",
    "bots.chatMenuLabel": "Actions for the group chat “{name}”",
    "bots.renameChat": "Rename…",
    "bots.renameChatHelp": "Changes the group's name for everyone in it — Enter saves, Escape cancels. Only the label changes: who is in it and what the agents remember stay as they are.",
    "bots.renameChatFailed": "Couldn't rename the group.",
    "bots.pinned": "Pinned",
    "bots.all": "All agents",
    "bots.newAgent": "New agent",
    "bots.newAgentHelp":
      "Creates another agent on this deployment, from a ready-made template or from scratch. You give it a name and pick what it should be good at; it runs on the deployment's default model until someone changes that in the agent's settings. Keyboard shortcut: ⌘N (Ctrl+N on Windows and Linux).",
    "bots.fold": "Hide the {name} list",
    "bots.unfold": "Show the {name} list",
    "bots.sectionHelp": "Click to fold or unfold. Right-click an agent for actions; right-click here or on empty space to make a new group.",
    "bots.pinnedEmpty": "Right-click an agent and choose Pin to keep it up here.",
    "bots.groupEmpty": "Empty — right-click an agent and choose “Move to {name}”.",
    "bots.allEmpty": "Every agent is pinned or in a group.",
    "bots.none": "No agents yet.",
    "bots.loading": "Loading agents…",
    "bots.open": "Open a chat with {name}",
    "bots.thinking": "Working on a reply",
    "bots.unread": "{n} new",
    "bots.menuLabel": "Actions for {name}",
    "bots.newChat": "New chat",
    "bots.newChatHelp": "Starts a fresh conversation with this agent and opens it.",
    "bots.allChats": "See all chats",
    "bots.allChatsHelp": "Opens the chat view with the list of every conversation you have had with this agent.",
    "bots.about": "About this agent",
    "bots.aboutHelp": "Opens the agent's page — profile, model, tools, channels and settings.",
    "bots.pin": "Pin",
    "bots.pinHelp":
      "Keeps this agent at the top of the list, under Pinned. A pinned agent leaves whichever group it was in — an agent is only ever in one place. Pins are remembered on this device only.",
    "bots.unpin": "Unpin",
    "bots.unpinHelp": "Sends this agent back to All agents.",
    "bots.pinButton":
      "Pin {name} — keeps it at the top of the list, under Pinned. A pinned agent leaves whichever group it was in; pins are remembered on this device only.",
    "bots.unpinButton": "Unpin {name} — sends it back to All agents.",
    "bots.moveUp": "Move up",
    "bots.moveDown": "Move down",
    "bots.moveHelp": "Rearranges the agents within their section. The order is remembered on this device only.",
    "bots.moveTo": "Move to “{name}”",
    "bots.moveToAll": "Move to All agents",
    "bots.moveToHelp":
      "Files this agent under that group. It leaves Pinned and any other group — an agent is only ever in one place. Groups change nothing about the agent itself.",
    "bots.markRead": "Mark {n} new as read",
    "bots.markReadHelp": "Clears the unread badge without opening the conversations.",
    "bots.newGroup": "New group…",
    "bots.newGroupHelp":
      "Adds a section to this list to sort agents into (“Sales”, “Personal”). Groups are yours and live on this device only — they change nothing about the agents. Type a name and press Enter; Escape cancels.",
    "bots.groupNamePlaceholder": "Group name…",
    "bots.groupMenuLabel": "Actions for the group “{name}”",
    "bots.sectionMenuLabel": "Actions for the agent list",
    "bots.rename": "Rename…",
    "bots.renameHelp": "Changes this group's name — Enter saves, Escape cancels. Only the label changes.",
    "bots.deleteGroup": "Delete group",
    "bots.deleteHelp": "Removes this section. Only an empty group can be deleted; Pinned and All agents can never be.",
    "bots.deleteBlocked": "Move the {n} agent(s) out of this group first — only an empty group can be deleted.",
    "bots.nameEmpty": "Give the group a name first.",
    "bots.nameDuplicate": "There is already a group with that name.",
    // ── the selected agent's chats, unfolded under its row (BotThreads.tsx) ──
    "bots.threadsLabel": "Chats with {name}",
    "bots.foldChats": "Hide {name}'s chats",
    "bots.unfoldChats": "Show {name}'s chats",
    "bots.chevHelp":
      "Shows or hides this agent's recent chats under its name without leaving the page you are on. Clicking the name itself opens the agent's latest conversation.",
    "bots.threadsLoading": "Loading chats…",
    "bots.threadsEmpty": "No chats yet — say something to start one.",
    "bots.threadsFilterEmpty": "No chats of this kind — pick another filter above.",
    "bots.threadRowHelp": "Click to open. Right-click to rename or delete.",
    "bots.showAll": "Show all {n} chats",
    "bots.showAllHelp":
      "Only the most recent chats are listed here. Opens the full list in the chat view, with search and multi-select delete.",
    "bots.threadMenuLabel": "Actions for the chat “{name}”",
    "bots.threadOpen": "Open",
    "bots.threadOpenHelp": "Opens this conversation in the chat view.",
    "bots.threadRename": "Rename…",
    "bots.threadRenameHelp":
      "Gives this conversation a name of your choosing — Enter saves, Escape cancels. Only the label changes; the messages and what the agent remembers stay as they are. Leave it empty to go back to the automatic title.",
    "bots.threadDelete": "Delete chat",
    "bots.threadDeleteHelp":
      "Removes this conversation and its messages for everyone, after a confirmation. The agent's memory files are not touched. Only an admin of this agent can delete.",
    "time.yesterday": "Yesterday",
  },
  zh: {
    "bots.chats": "群聊",
    "bots.chatsEmpty": "还没有群聊。",
    "bots.chatsLoading": "正在加载群聊…",
    "bots.chatsMenuLabel": "群聊的操作",
    "bots.newChatGroup": "新建群聊…",
    "bots.newChatGroupHelp":
      "开始一段同时包含 2–6 个智能体的对话。群里的每个智能体都会读到每条消息；你选的第一个智能体回答没有点名的消息，其余智能体在你 @提及 时回复。这与此列表中的分组不同——分组只用于整理智能体。",
    "bots.newChatGroupNeedsTwo": "至少需要两个智能体才能开始群聊。",
    "bots.openChat": "打开",
    "bots.openChatHelp": "打开这个群聊。",
    "bots.openChatTitle": "{name}——{members}",
    "bots.sharedChat": "已共享",
    "bots.sharedChatHelp":
      "这个群聊由其他人创建并共享给了你。你可以阅读和发言；只有创建者和管理员才能重命名、更改智能体、继续共享或删除它。打开群聊信息可以退出。",
    "bots.chatMenuLabel": "群聊“{name}”的操作",
    "bots.renameChat": "重命名…",
    "bots.renameChatHelp": "为群里的所有人更改群名——Enter 保存，Escape 取消。只改名称：成员和智能体的记忆都不变。",
    "bots.renameChatFailed": "无法重命名群聊。",
    "bots.pinned": "已置顶",
    "bots.all": "全部智能体",
    "bots.newAgent": "新建智能体",
    "bots.newAgentHelp":
      "在此部署上创建另一个智能体，可使用现成模板或从零开始。你为它起名并选择它擅长什么；在有人于智能体设置中更改之前，它使用部署的默认模型。快捷键：⌘N（Windows 和 Linux 上为 Ctrl+N）。",
    "bots.fold": "隐藏“{name}”列表",
    "bots.unfold": "显示“{name}”列表",
    "bots.sectionHelp": "点击折叠或展开。右键点击智能体查看操作；在此处或空白处右键可新建分组。",
    "bots.pinnedEmpty": "右键点击智能体并选择“置顶”，即可固定在此。",
    "bots.groupEmpty": "空的——右键点击智能体并选择“移动到 {name}”。",
    "bots.allEmpty": "所有智能体都已置顶或在分组中。",
    "bots.none": "还没有智能体。",
    "bots.loading": "正在加载智能体…",
    "bots.open": "打开与 {name} 的聊天",
    "bots.thinking": "正在回复",
    "bots.unread": "{n} 条新消息",
    "bots.menuLabel": "{name} 的操作",
    "bots.newChat": "新聊天",
    "bots.newChatHelp": "与此智能体开始一段新对话并打开它。",
    "bots.allChats": "查看全部聊天",
    "bots.allChatsHelp": "打开聊天视图，并列出你与此智能体的所有对话。",
    "bots.about": "关于此智能体",
    "bots.aboutHelp": "打开智能体页面——资料、模型、工具、渠道和设置。",
    "bots.pin": "置顶",
    "bots.pinHelp": "把此智能体固定在列表顶部的“已置顶”下。置顶后它会离开原来所在的分组——一个智能体只会出现在一个位置。置顶仅记录在本设备上。",
    "bots.unpin": "取消置顶",
    "bots.unpinHelp": "把此智能体放回“全部智能体”。",
    "bots.pinButton": "置顶 {name}——固定在列表顶部的“已置顶”下。置顶后它会离开原来所在的分组；置顶仅记录在本设备上。",
    "bots.unpinButton": "取消置顶 {name}——放回“全部智能体”。",
    "bots.moveUp": "上移",
    "bots.moveDown": "下移",
    "bots.moveHelp": "在所在区域内调整智能体的顺序。顺序仅记录在本设备上。",
    "bots.moveTo": "移动到“{name}”",
    "bots.moveToAll": "移动到“全部智能体”",
    "bots.moveToHelp": "把此智能体归入该分组。它会离开“已置顶”和其他分组——一个智能体只会出现在一个位置。分组不会改变智能体本身。",
    "bots.markRead": "将 {n} 条新消息标为已读",
    "bots.markReadHelp": "清除未读标记，但不打开对话。",
    "bots.newGroup": "新建分组…",
    "bots.newGroupHelp": "在此列表中添加一个区域来归类智能体（“销售”“个人”）。分组只属于你并仅保存在本设备上——不会改变智能体本身。输入名称后按 Enter；Escape 取消。",
    "bots.groupNamePlaceholder": "分组名称…",
    "bots.groupMenuLabel": "分组“{name}”的操作",
    "bots.sectionMenuLabel": "智能体列表的操作",
    "bots.rename": "重命名…",
    "bots.renameHelp": "更改此分组的名称——Enter 保存，Escape 取消。只改标签。",
    "bots.deleteGroup": "删除分组",
    "bots.deleteHelp": "移除此区域。只能删除空分组；“已置顶”和“全部智能体”永远不能删除。",
    "bots.deleteBlocked": "请先把分组中的 {n} 个智能体移出——只能删除空分组。",
    "bots.nameEmpty": "请先为分组起个名称。",
    "bots.nameDuplicate": "已有同名分组。",
    "bots.threadsLabel": "与 {name} 的对话",
    "bots.foldChats": "收起 {name} 的对话",
    "bots.unfoldChats": "展开 {name} 的对话",
    "bots.chevHelp": "在不离开当前页面的情况下，展开或收起此智能体最近的对话。点击名称本身会打开该智能体最新的对话。",
    "bots.threadsLoading": "正在加载对话…",
    "bots.threadsEmpty": "还没有对话——说点什么就能开始。",
    "bots.threadsFilterEmpty": "没有这类对话——请在上方换一个筛选。",
    "bots.threadRowHelp": "点击打开。右键可重命名或删除。",
    "bots.showAll": "查看全部 {n} 个对话",
    "bots.showAllHelp": "这里仅列出最近的对话。点击可在聊天视图中打开完整列表，支持搜索和多选删除。",
    "bots.threadMenuLabel": "对话“{name}”的操作",
    "bots.threadOpen": "打开",
    "bots.threadOpenHelp": "在聊天视图中打开此对话。",
    "bots.threadRename": "重命名…",
    "bots.threadRenameHelp": "为此对话起一个你自己的名字——Enter 保存，Escape 取消。只改标签：消息和智能体的记忆都不变。留空则恢复自动标题。",
    "bots.threadDelete": "删除对话",
    "bots.threadDeleteHelp": "确认后为所有人删除此对话及其消息。不会触及智能体的记忆文件。只有此智能体的管理员可以删除。",
    "time.yesterday": "昨天",
  },
  es: {
    "bots.chats": "Chats de grupo",
    "bots.chatsEmpty": "Aún no hay chats de grupo.",
    "bots.chatsLoading": "Cargando chats de grupo…",
    "bots.chatsMenuLabel": "Acciones para los chats de grupo",
    "bots.newChatGroup": "Nuevo chat de grupo…",
    "bots.newChatGroupHelp":
      "Inicia una conversación con 2–6 de tus agentes a la vez. Todos los agentes leen cada mensaje; el primero que elijas responde a lo que no nombra a nadie, y los demás responden cuando los @mencionas. No es lo mismo que un grupo de esta lista: esos solo ordenan agentes.",
    "bots.newChatGroupNeedsTwo": "Necesitas al menos dos agentes para empezar un chat de grupo.",
    "bots.openChat": "Abrir",
    "bots.openChatHelp": "Abre esta conversación de grupo.",
    "bots.openChatTitle": "{name} — {members}",
    "bots.sharedChat": "Compartido",
    "bots.sharedChatHelp":
      "Otra persona creó este chat de grupo y lo compartió contigo. Puedes leerlo y escribir en él; solo quien lo creó y los administradores pueden renombrarlo, cambiar sus agentes, compartirlo con más gente o eliminarlo. Abre sus detalles para salir de él.",
    "bots.chatMenuLabel": "Acciones para el chat de grupo «{name}»",
    "bots.renameChat": "Renombrar…",
    "bots.renameChatHelp": "Cambia el nombre del grupo para todos sus miembros: Intro guarda, Escape cancela. Solo cambia la etiqueta; quién está dentro y lo que recuerdan los agentes no cambia.",
    "bots.renameChatFailed": "No se pudo renombrar el grupo.",
    "bots.pinned": "Fijados",
    "bots.all": "Todos los agentes",
    "bots.newAgent": "Nuevo agente",
    "bots.newAgentHelp":
      "Crea otro agente en esta instalación, a partir de una plantilla o desde cero. Le pones un nombre y eliges en qué debe ser bueno; usa el modelo por defecto de la instalación hasta que alguien lo cambie en los ajustes del agente. Atajo: ⌘N (Ctrl+N en Windows y Linux).",
    "bots.fold": "Ocultar la lista {name}",
    "bots.unfold": "Mostrar la lista {name}",
    "bots.sectionHelp": "Clic para plegar o desplegar. Clic derecho en un agente para ver acciones; clic derecho aquí o en el espacio vacío para crear un grupo.",
    "bots.pinnedEmpty": "Haz clic derecho en un agente y elige Fijar para tenerlo aquí arriba.",
    "bots.groupEmpty": "Vacío — haz clic derecho en un agente y elige «Mover a {name}».",
    "bots.allEmpty": "Todos los agentes están fijados o en un grupo.",
    "bots.none": "Aún no hay agentes.",
    "bots.loading": "Cargando agentes…",
    "bots.open": "Abrir un chat con {name}",
    "bots.thinking": "Preparando una respuesta",
    "bots.unread": "{n} nuevos",
    "bots.menuLabel": "Acciones para {name}",
    "bots.newChat": "Nuevo chat",
    "bots.newChatHelp": "Empieza una conversación nueva con este agente y la abre.",
    "bots.allChats": "Ver todos los chats",
    "bots.allChatsHelp": "Abre la vista de chat con la lista de todas las conversaciones que has tenido con este agente.",
    "bots.about": "Sobre este agente",
    "bots.aboutHelp": "Abre la página del agente: perfil, modelo, herramientas, canales y ajustes.",
    "bots.pin": "Fijar",
    "bots.pinHelp":
      "Mantiene este agente arriba de la lista, bajo Fijados. Un agente fijado sale del grupo en el que estuviera: un agente solo está en un sitio. Los fijados se recuerdan solo en este dispositivo.",
    "bots.unpin": "Desfijar",
    "bots.unpinHelp": "Devuelve este agente a Todos los agentes.",
    "bots.pinButton":
      "Fijar {name}: lo mantiene arriba de la lista, bajo Fijados. Un agente fijado sale del grupo en el que estuviera; los fijados se recuerdan solo en este dispositivo.",
    "bots.unpinButton": "Desfijar {name}: lo devuelve a Todos los agentes.",
    "bots.moveUp": "Subir",
    "bots.moveDown": "Bajar",
    "bots.moveHelp": "Reordena los agentes dentro de su sección. El orden se recuerda solo en este dispositivo.",
    "bots.moveTo": "Mover a «{name}»",
    "bots.moveToAll": "Mover a Todos los agentes",
    "bots.moveToHelp":
      "Archiva este agente bajo ese grupo. Sale de Fijados y de cualquier otro grupo: un agente solo está en un sitio. Los grupos no cambian nada del agente en sí.",
    "bots.markRead": "Marcar {n} nuevos como leídos",
    "bots.markReadHelp": "Quita la marca de no leído sin abrir las conversaciones.",
    "bots.newGroup": "Nuevo grupo…",
    "bots.newGroupHelp":
      "Añade una sección a esta lista para ordenar agentes («Ventas», «Personal»). Los grupos son tuyos y viven solo en este dispositivo: no cambian nada de los agentes. Escribe un nombre y pulsa Intro; Escape cancela.",
    "bots.groupNamePlaceholder": "Nombre del grupo…",
    "bots.groupMenuLabel": "Acciones para el grupo «{name}»",
    "bots.sectionMenuLabel": "Acciones para la lista de agentes",
    "bots.rename": "Renombrar…",
    "bots.renameHelp": "Cambia el nombre de este grupo: Intro guarda, Escape cancela. Solo cambia la etiqueta.",
    "bots.deleteGroup": "Eliminar grupo",
    "bots.deleteHelp": "Quita esta sección. Solo se puede eliminar un grupo vacío; Fijados y Todos los agentes no se pueden eliminar nunca.",
    "bots.deleteBlocked": "Saca primero los {n} agente(s) de este grupo: solo se puede eliminar un grupo vacío.",
    "bots.nameEmpty": "Ponle un nombre al grupo primero.",
    "bots.nameDuplicate": "Ya hay un grupo con ese nombre.",
    "bots.threadsLabel": "Chats con {name}",
    "bots.foldChats": "Ocultar los chats de {name}",
    "bots.unfoldChats": "Mostrar los chats de {name}",
    "bots.chevHelp":
      "Muestra u oculta los chats recientes de este agente bajo su nombre sin salir de la página en la que estás. Hacer clic en el nombre abre la conversación más reciente del agente.",
    "bots.threadsLoading": "Cargando chats…",
    "bots.threadsEmpty": "Aún no hay chats — escribe algo para empezar uno.",
    "bots.threadsFilterEmpty": "No hay chats de este tipo — elige otro filtro arriba.",
    "bots.threadRowHelp": "Clic para abrir. Clic derecho para renombrar o eliminar.",
    "bots.showAll": "Ver los {n} chats",
    "bots.showAllHelp":
      "Aquí solo aparecen los chats más recientes. Abre la lista completa en la vista de chat, con búsqueda y eliminación múltiple.",
    "bots.threadMenuLabel": "Acciones para el chat «{name}»",
    "bots.threadOpen": "Abrir",
    "bots.threadOpenHelp": "Abre esta conversación en la vista de chat.",
    "bots.threadRename": "Renombrar…",
    "bots.threadRenameHelp":
      "Da a esta conversación el nombre que quieras: Intro guarda, Escape cancela. Solo cambia la etiqueta; los mensajes y lo que recuerda el agente no cambian. Déjalo vacío para volver al título automático.",
    "bots.threadDelete": "Eliminar chat",
    "bots.threadDeleteHelp":
      "Elimina esta conversación y sus mensajes para todos, tras una confirmación. Los archivos de memoria del agente no se tocan. Solo un administrador de este agente puede eliminar.",
    "time.yesterday": "Ayer",
  },
};
